import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { AppearancePanel } from '@diwata/ironvale'
import { DiwaSigil } from './DiwaSigil'
import { useRegistrationOpen } from '@/lib/registrationStatus'

/**
 * THE one marketing shell — the single header (and footer) shared by the Landing
 * and every public page (/site/*, /roadmap, /changelog, /terms, /privacy). Kills
 * the old Landing-topbar vs SiteShell split: one nav, one voice, one sigil.
 *
 * Nav: Diwa Domains (drawn sigil) · How it works · Domains · Springs · Roadmap ·
 * Changelog | Sign in · Create account · Aa. Mobile collapses the links into the
 * accessible hamburger disclosure (Escape / outside-click / select-to-close,
 * focus moved in and restored).
 *
 * Auth actions target /login today; the copy is already phrased for the one-login
 * canon ("sign in with your Diwata ID") so the Gate handoff swaps a target, not
 * the language.
 */

const LINKS = [
  { to: '/site/how', label: 'How it works' },
  { to: '/site/domains', label: 'Domains' },
  { to: '/site/springs', label: 'Springs' },
  { to: '/roadmap', label: 'Roadmap' },
  { to: '/changelog', label: 'Changelog' },
]

export function MarketingShell({ children }: { children: React.ReactNode }) {
  // Waitlist-first until the registration flag opens (founder ruling 2026-08-06):
  // the nav's account CTA obeys the same one story as the landing's.
  const registrationOpen = useRegistrationOpen()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const hamburgerRef = useRef<HTMLButtonElement>(null)
  const location = useLocation()

  const closeMenu = () => setMenuOpen(false)

  // route change closes the disclosure
  useEffect(() => setMenuOpen(false), [location.pathname])

  useEffect(() => {
    if (!menuOpen) return
    menuRef.current?.querySelector<HTMLElement>('a, button')?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        hamburgerRef.current?.focus()
      }
    }
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (menuRef.current?.contains(t) || hamburgerRef.current?.contains(t)) return
      setMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [menuOpen])

  return (
    <div className="dwl">
      <nav className="dwl-top" aria-label="Site">
        <Link to="/" className="dwl-top-brand">
          <DiwaSigil size={22} />
          Diwa Domains
        </Link>
        <span className="dwl-top-links">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => (isActive ? 'is-here' : undefined)}>
              {l.label}
            </NavLink>
          ))}
          <span className="dwl-top-sep" aria-hidden="true" />
          <Link to="/login" className="dwl-top-signin">Sign in</Link>
          {registrationOpen ? (
            <Link to="/login" className="dwl-top-enter">Create account</Link>
          ) : (
            <Link to="/waitlist" className="dwl-top-enter">Join the waitlist</Link>
          )}
          <AppearancePanel className="dwl-appearance" />
          <button
            ref={hamburgerRef}
            type="button"
            className="dwl-hamburger"
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls="dwl-mobile-menu"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              {menuOpen ? (
                <>
                  <line x1="4" y1="4" x2="16" y2="16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <line x1="16" y1="4" x2="4" y2="16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="17" y2="6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <line x1="3" y1="14" x2="17" y2="14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </>
              )}
            </svg>
          </button>
        </span>
        {menuOpen && (
          <div id="dwl-mobile-menu" className="dwl-mobile-menu" ref={menuRef}>
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="dwl-mobile-menu-link" onClick={closeMenu}>
                {l.label}
              </Link>
            ))}
            <Link to="/login" className="dwl-mobile-menu-link" onClick={closeMenu}>Sign in</Link>
            {registrationOpen ? (
              <Link to="/login" className="dwl-mobile-menu-link dwl-mobile-menu-link--primary" onClick={closeMenu}>
                Create account
              </Link>
            ) : (
              <Link to="/waitlist" className="dwl-mobile-menu-link dwl-mobile-menu-link--primary" onClick={closeMenu}>
                Join the waitlist
              </Link>
            )}
          </div>
        )}
      </nav>

      {children}

      <footer className="dwl-footer">
        <span>
          <span className="dwl-pip dwl-pip--quiet" aria-hidden="true" /> Diwata LLC · diwa.domains · the keeper owns their graph.
        </span>
        <span className="dwl-footer-links">
          <Link className="dwl-footer-link" to="/terms">Terms</Link>
          <Link className="dwl-footer-link" to="/privacy">Privacy</Link>
          <a className="dwl-footer-link" href="https://diwata.domains" target="_blank" rel="noreferrer">by Diwata ✦</a>
        </span>
      </footer>
    </div>
  )
}
